/**
 * Linda JWT Token Manager
 *
 * Fetches and caches JWT tokens from the Floorplanner backend for
 * authenticating with the LiteLLM gateway. Tokens are valid for 2 hours;
 * we refresh proactively at 1h50m to avoid mid-request expiry.
 *
 * Ported from linda-editor/src/lib/linda-jwt.ts
 */

import {logger} from '@/lib/logger';

const log = logger.tag('LindaJWT');

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

/** How early (ms) before expiry to refresh. 10 minutes. */
const REFRESH_MARGIN_MS = 10 * 60 * 1000;

/** Linda API base — Vite proxy in dev, direct in prod */
const LINDA_API_URL = import.meta.env.DEV
    ? '/fp-linda'
    : 'https://floorplanner.com/api/linda/v1';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

/** The Floorplanner auth token used to request JWTs */
let fpAuthToken: string | null = null;

/** Cached JWT for LiteLLM */
let cachedJwt: string | null = null;

/** When the cached JWT expires (epoch ms) */
let jwtExpiresAt = 0;

/** In-flight fetch promise (dedup concurrent calls) */
let fetchPromise: Promise<string> | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Set the Floorplanner auth token. Called once when the user context loads.
 */
export function setFpAuthToken(token: string | undefined | null): void {
    fpAuthToken = token ?? null;
}

/**
 * Get a valid LiteLLM API key (JWT token).
 * Fetches/refreshes a JWT from the Floorplanner backend as needed.
 */
export async function getLiteLLMApiKey(): Promise<string> {
    // If we have a valid cached JWT, return it
    if (cachedJwt && Date.now() < jwtExpiresAt - REFRESH_MARGIN_MS) {
        return cachedJwt;
    }

    // Fetch a new one (dedup concurrent callers)
    fetchPromise ??= fetchJwt().finally(() => {
        fetchPromise = null;
    });

    return fetchPromise;
}

// ---------------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------------

async function fetchJwt(): Promise<string> {
    if (!fpAuthToken) {
        throw new Error(
            'No Floorplanner auth token available — cannot fetch LiteLLM JWT'
        );
    }

    const url = `${LINDA_API_URL}/chat.json`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${fpAuthToken}`,
        },
    });

    if (!response.ok) {
        const msg = `JWT fetch failed: ${response.status}`;
        log.error(msg);
        throw new Error(msg);
    }

    const data = (await response.json()) as {token?: string};

    if (!data.token) {
        const msg = 'JWT response missing token field';
        log.error(msg);
        throw new Error(msg);
    }

    cachedJwt = data.token;

    // Parse exp claim from JWT to set expiry, fallback to 2h from now
    const exp = parseJwtExp(data.token);
    jwtExpiresAt = exp ? exp * 1000 : Date.now() + 2 * 60 * 60 * 1000;

    log.debug('JWT fetched', {
        expiresIn: Math.round((jwtExpiresAt - Date.now()) / 1000 / 60) + 'min',
    });

    return data.token;
}

/**
 * Extract the `exp` claim from a JWT without a crypto library.
 * Returns epoch seconds, or null if parsing fails.
 */
function parseJwtExp(jwt: string): number | null {
    try {
        const payload = jwt.split('.')[1];
        if (!payload) return null;
        const decoded = JSON.parse(atob(payload)) as {exp?: number};
        return decoded.exp ?? null;
    } catch {
        return null;
    }
}
